import { useState, useEffect, useCallback } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import type { BatchHistoryItem, DateRange } from '../types';
import { listBatchHistory } from '../api';
import { DateRangePicker } from '../../components/DateRangePicker';
import { createLogger } from '../../utils/logger';

const log = createLogger('InteriorBatchHistoryTable');

// Inline copy of `formatCost` so this table doesn't need lightning's
// pricing helpers (interior costs are pre-summed by the backend from
// each job's FAL `direct_cost`).
function formatCost(cost: number | null | undefined): string {
  if (cost == null || cost === 0) return '-';
  if (cost < 0.01) return '<$0.01';
  return `$${cost.toFixed(2)}`;
}

const columnHelper = createColumnHelper<BatchHistoryItem>();

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
}

const columns = [
  columnHelper.accessor('batch_id', {
    header: 'Batch ID',
    cell: (info) => {
      const value = info.getValue();
      return (
        <span className="font-mono text-xs text-slate-500" title={value}>
          {value.substring(0, 8)}...
        </span>
      );
    },
  }),
  columnHelper.accessor('filename', {
    header: 'CSV File',
    cell: (info) => (
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span className="font-medium text-slate-900">{info.getValue()}</span>
      </div>
    ),
  }),
  columnHelper.accessor('total_scans', {
    header: 'Total',
    cell: (info) => (
      <span className="font-mono text-slate-700">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor('completed', {
    header: 'Completed',
    cell: (info) => (
      <span className="font-mono text-green-600 font-medium">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor('failed', {
    header: 'Failed',
    cell: (info) => {
      const value = info.getValue();
      if (value === 0) {
        return <span className="text-slate-400">0</span>;
      }
      return <span className="font-mono text-red-600 font-medium">{value}</span>;
    },
  }),
  columnHelper.accessor('success_percentage', {
    header: 'Success Rate',
    cell: (info) => {
      const value = info.getValue();
      return (
        <span className={`font-mono font-medium ${value >= 90 ? 'text-green-600' : value >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
          {value.toFixed(0)}%
        </span>
      );
    },
  }),
  columnHelper.accessor('duration_seconds', {
    header: 'Duration',
    cell: (info) => (
      <span className="font-mono text-slate-600">{formatDuration(info.getValue())}</span>
    ),
  }),
  columnHelper.accessor('completed_at', {
    header: 'Completed At',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return '-';
      try {
        return (
          <span className="text-slate-600">
            {format(new Date(value), 'MMM d, yyyy HH:mm')}
          </span>
        );
      } catch {
        return '-';
      }
    },
  }),
  columnHelper.accessor('total_cost', {
    header: 'Total Cost',
    cell: (info) => {
      const value = info.getValue();
      if (value === undefined || value === null) {
        return <span className="text-slate-400">-</span>;
      }
      return (
        <span className="font-mono text-sm text-slate-700 font-medium">
          {formatCost(value)}
        </span>
      );
    },
  }),
];

/**
 * Interior batch-history table — 1:1 functional clone of lightning's
 * `BatchHistoryTable`. Same columns (CSV file, total/completed/failed
 * counts, success rate, duration, completed_at, total cost) and same
 * date-range filtering + pagination.
 */
export function BatchHistoryTable() {
  const [batches, setBatches] = useState<BatchHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>({ from: null, to: null });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listBatchHistory(dateRange, page, pageSize);
      setBatches(response.batches);
      setTotalPages(response.total_pages);
      setTotal(response.total);
    } catch (error) {
      log.error('Failed to load batch history:', error);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    setPage(1);
  };

  const table = useReactTable({
    data: batches,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-soft overflow-hidden transition-all duration-200">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Batch History</h2>
            <p className="text-sm text-slate-500">
              {total} completed batch{total !== 1 ? 'es' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-nowrap sm:flex-wrap overflow-x-auto pb-1 sm:pb-0 scrollbar-hide">
          <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />

          <button
            onClick={loadData}
            title="Apply Filters"
            className="btn-secondary px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5 shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span className="hidden sm:inline">Apply Filters</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="p-12 text-center">
          <div className="inline-flex items-center gap-3 text-slate-500">
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading...
          </div>
        </div>
      ) : batches.length === 0 ? (
        <div className="p-12 text-center">
          <div className="inline-flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
              <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div>
              <p className="text-slate-900 font-medium">No completed batches</p>
              <p className="text-sm text-slate-500">Completed batch runs will appear here</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-5 py-3 text-left"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-100">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-5 py-3"
                      data-label={typeof cell.column.columnDef.header === 'string' ? cell.column.columnDef.header : undefined}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="btn-secondary px-2.5 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              First
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary px-2.5 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-2.5 py-1 text-xs text-slate-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn-secondary px-2.5 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="btn-secondary px-2.5 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
